/**
 * Escalations: things that went wrong badly enough that a person has to be told, and has to say they have seen it.
 *
 * Server contract (built in parallel; a missing route is an empty list, never an error):
 *   GET  /api/workspaces/:id/escalations?status=open
 *        -> [{id, level: "P0" | "P1" | "info", title, body, channels: [{channel, status, at, detail}], status, createdAt, deliveredAt}]
 *   POST /api/workspaces/:id/escalations/:id/ack
 *
 * A P0 stays pinned at the top of Content Studio and Draper until it is acknowledged. "A failed notification is itself a P0":
 * when no channel got the alert through, the banner says so instead of showing a calm tick.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { errorMessage } from "@/components/content-simple/use-studio";

export type EscalationLevel = "P0" | "P1" | "info";
export type DeliveryState = "delivered" | "failed" | "pending" | "skipped";

export interface EscalationChannel {
  channel: string;
  /** The server's own word for it ("sent", "failed" ...). */
  status: string;
  state: DeliveryState;
  at: string | null;
  detail: string | null;
}

export interface Escalation {
  id: number | string;
  level: EscalationLevel;
  title: string;
  body: string;
  channels: EscalationChannel[];
  status: string;
  createdAt: string | null;
  deliveredAt: string | null;
}

/** Failure words win over success words ("not_delivered" contains "delivered"); anything unrecognised is still in flight. "suppressed" is the server's sandbox guard: nothing was attempted, so it is never "still sending". */
export function deliveryStateOf(status: unknown): DeliveryState {
  const s = String(status ?? "").toLowerCase().replace(/[_-]+/g, " ");
  if (/fail|error|bounce|reject|undeliver|blocked|denied|time ?out|\bnot (sent|delivered)\b/.test(s)) return "failed";
  if (/\bskip|\bsuppress|not configured|disabled|unavailable|\bn\/a\b|^none$/.test(s)) return "skipped";
  if (/\b(sent|delivered|ok|success|successful|confirmed|acked?|accepted)\b/.test(s)) return "delivered";
  return "pending";
}

function levelOf(v: unknown): EscalationLevel {
  const s = String(v ?? "").trim().toUpperCase();
  return s === "P0" ? "P0" : s === "P1" ? "P1" : "info";
}

const LEVEL_ORDER: Record<EscalationLevel, number> = { P0: 0, P1: 1, info: 2 };

/** "thread" (a note in the Draper chat) and "log" are records the server keeps, not ways of reaching a person: an alert that only landed there has not been delivered. */
const INTERNAL_CHANNELS = new Set(["thread", "log"]);

function strOrNull(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

export function normalizeEscalations(json: unknown): Escalation[] {
  const list = Array.isArray(json) ? json : json && typeof json === "object" && Array.isArray((json as any).escalations) ? (json as any).escalations : [];
  const out: Escalation[] = [];
  for (const raw of list as any[]) {
    if (!raw || typeof raw !== "object" || raw.id === undefined || raw.id === null) continue;
    const channels: EscalationChannel[] = (Array.isArray(raw.channels) ? raw.channels : [])
      .filter((c: any) => c && typeof c === "object" && c.channel && !INTERNAL_CHANNELS.has(String(c.channel).toLowerCase()))
      .map((c: any) => ({
        channel: String(c.channel),
        status: String(c.status ?? ""),
        state: deliveryStateOf(c.status),
        at: strOrNull(c.at),
        detail: strOrNull(c.detail),
      }));
    out.push({
      id: raw.id,
      level: levelOf(raw.level),
      title: strOrNull(raw.title) ?? "Something needs your attention",
      body: typeof raw.body === "string" ? raw.body.trim() : "",
      channels,
      status: String(raw.status ?? "open"),
      createdAt: strOrNull(raw.createdAt),
      deliveredAt: strOrNull(raw.deliveredAt),
    });
  }
  // P0 first, then P1, then info; the newest first within a level.
  return out.sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level] || (Date.parse(b.createdAt ?? "") || 0) - (Date.parse(a.createdAt ?? "") || 0));
}

/** "Telegram" and "email", as the banner reads. */
export function channelName(channel: string): string {
  const c = channel.trim().toLowerCase();
  if (c === "telegram") return "Telegram";
  if (c === "email") return "email";
  return c || "channel";
}

/** At least one outside channel confirmed delivery (the server stamps deliveredAt then). When it is false, nobody has been told. */
export function reachedAnyone(e: Escalation): boolean {
  return e.deliveredAt !== null || e.channels.some((c) => c.state === "delivered");
}

/** "3:42 PM" for today, "Oct 4, 3:42 PM" otherwise. */
export function sinceText(iso: string | null, now: number = Date.now()): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date(now);
  const sameDay = d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
  return d.toLocaleString(undefined, sameDay ? { hour: "numeric", minute: "2-digit" } : { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const key = (wsId: number | null) => ["/api/workspaces", wsId, "escalations", "open"] as const;

interface EscalationsData {
  available: boolean;
  list: Escalation[];
}

/** Open escalations for the active workspace. Polls once a minute (and when the tab is refocused) so a P0 shows up without a reload. */
export function useOpenEscalations() {
  const { activeWorkspaceId: wsId } = useWorkspaceStore();
  const q = useQuery<EscalationsData>({
    queryKey: key(wsId),
    enabled: !!wsId,
    staleTime: 20_000,
    refetchOnWindowFocus: true,
    // A server without the route will not grow one while the page is open: stop asking.
    refetchInterval: (query) => (query.state.data?.available === false ? false : 60_000),
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${wsId}/escalations?status=open`, { credentials: "include" });
      if (res.status === 404 || res.status === 405 || res.status === 501) return { available: false, list: [] };
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return { available: true, list: normalizeEscalations(await res.json()) };
    },
  });
  const escalations = q.data?.list ?? [];
  return {
    escalations,
    p0: escalations.filter((e) => e.level === "P0"),
    available: q.data?.available ?? true,
    isLoading: q.isLoading,
    isError: q.isError && !q.data,
  };
}

export function useAckEscalation() {
  const { activeWorkspaceId: wsId } = useWorkspaceStore();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (id: number | string) => (await apiRequest("POST", `/api/workspaces/${wsId}/escalations/${id}/ack`, {})).json().catch(() => ({})),
    onSuccess: (_d, id) => {
      // Drop it from the list at once; the refetch confirms.
      queryClient.setQueryData<EscalationsData>(key(wsId), (old) => (old ? { ...old, list: old.list.filter((e) => String(e.id) !== String(id)) } : old));
      queryClient.invalidateQueries({ queryKey: key(wsId) });
      toast({ title: "Acknowledged", description: "It no longer shows as open." });
    },
    onError: (e) => toast({ title: "Couldn't acknowledge it", description: errorMessage(e, "Please try again."), variant: "destructive" }),
  });
}
